import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@linaria/core';
import { SelectField } from 'components/ui_library/Formik';
import { Formik } from 'formik';
import cogoToast from 'components/ToastMessage/ToastMessage';
import { Button } from 'components/Button';
import { either, taskEither } from 'fp-ts';
import { queryKeys, useQueryTE } from 'utils/react-query';
import { useLocalStorage } from 'react-use';
import { errorToReactLeft, pipe } from 'utils/fp';
import { listAppsTE } from 'api/apps';
import { prop } from 'rambda';
import { listAppDatasetsTE } from 'api/datasets/listDatasets';
import router, { useRouter } from 'next/router';
import { useGRPCClient } from 'api/grpc/client';
import { Filter, Input, Rank } from 'api/grpc/resources';
import { fetchInputsCounts } from 'api/inputs/fetchInputsCounts';
import { SelectOptions } from 'modules/Models/components/ModelForm/components/FormComponentRenderer/FormComponents/SelectFormElement';
import { useCurrentUserId } from 'modules/Auth/hooks';
const apiEffects = {
  listAllOwnedApps: (userId: string) => pipe(listAppsTE({
    userId,
    params: 'per_page=500'
  }, errorToReactLeft), taskEither.map(prop('apps'))),
  fetchAppDatasets: (appId: string, userId: string) => pipe(listAppDatasetsTE({
    appId,
    userId
  }, errorToReactLeft), taskEither.map(resp => resp.datasets))
};
export const BatchPredictModal = ({
  closeBatchPredictArea
}: {
  closeBatchPredictArea: () => void;
}) => {
  const userId = useCurrentUserId();
  const {
    userOrOrgId,
    appId,
    modelId,
    workflowId
  } = (useRouter().query as {
    userOrOrgId: string;
    appId: string;
    modelId: string;
    workflowId: string;
  });
  const [, setPredictData] = useLocalStorage('predict_data', '');
  const grpcClient = useGRPCClient();
  const [selectedApp, setSelectedApp] = useState('');
  const [selectedDataset, setSelectedDataset] = useState('');
  const [totalInputCount, setTotalInputCount] = useState('0');
  const [selectedDatasetCount, setSelectedDatasetCount] = useState<number | undefined>(undefined);
  const {
    data: ownedAppsE
  } = useQueryTE([queryKeys.Apps, {
    userId
  }], apiEffects.listAllOwnedApps(userId || ''), {
    enabled: Boolean(userId)
  });
  const {
    data: appDatasets
  } = useQueryTE(['Datasets', {
    selectedApp
  }], apiEffects.fetchAppDatasets((selectedApp as string), (userId as string)), {
    enabled: Boolean(selectedApp)
  });
  const appsOptions = pipe(ownedAppsE, either.fold(() => [], apps => apps.map(app => ({
    name: app.name,
    value: app.id
  }))));
  const datasetOptions = pipe(appDatasets, either.fold(() => [], datasets => datasets.map(dataset => ({
    id: dataset.id,
    value: dataset.id
  }))));
  const onAppsChange = useCallback(e => {
    setSelectedApp(e.value);
  }, []);
  const onDatasetsChange = useCallback(async e => {
    setSelectedDataset(e.value);
    if (e.value.includes('default_dataset')) {
      setSelectedDataset('');
      setSelectedDatasetCount(Number.parseInt(totalInputCount || '0', 10));
      return;
    }
    try {
      const datasetFilter = new Input();
      datasetFilter.setDatasetIdsList([e.value]);
      const filters = new Filter();
      filters.setInput(datasetFilter);
      const ranks: Rank[] = [];
      const resp = await fetchInputsCounts({
        userOrOrgId: userId || '',
        appId: selectedApp,
        filters: [filters],
        ranks
      });
      const datasetInputs = resp.hit_counts[0].estimated_total;
      if (datasetInputs !== '0') {
        setSelectedDatasetCount(Number.parseInt(datasetInputs, 10));
      }
    } catch (error) {
      console.error(error);
    }
  }, [totalInputCount, selectedApp]);
  const onTryInputClick = useCallback(async () => {
    // Set predict data in local storage to fetch in InputViewer
    setPredictData(JSON.stringify({
      userOrOrgId,
      appId,
      modelId,
      workflowId
    }));
    const filter = new Filter();
    const input = new Input();
    if (selectedDataset) input.setDatasetIdsList([selectedDataset]);
    filter.setInput(input);
    const filters = [filter];
    const ranks: Rank[] = [];
    const inputsSearchesResp = await grpcClient.postInputsSearches({
      userOrOrgId: userId || '',
      appId: selectedApp,
      page: 1,
      perPage: 50,
      filters,
      ranks
    });
    const inputsResp = pipe(inputsSearchesResp, either.fold(() => [], resp => resp.getHitsList().map(hit => hit.getInput())));
    const firstInput = inputsResp.map(searchInput => searchInput?.getId())[0];
    if (!firstInput) {
      cogoToast.error('No inputs found for this dataset. Please try another dataset.');
      return;
    }
    router.push(`/${userId}/${selectedApp}/inputs/${firstInput}?currentIndex=0`);
  }, [userId, selectedApp, selectedDataset]);
  const fetchInputsCount = useCallback(async () => {
    const datasetFilter = new Input();
    datasetFilter.setDatasetIdsList([]);
    const filters = new Filter();
    filters.setInput(datasetFilter);
    const ranks: Rank[] = [];
    const resp = await fetchInputsCounts({
      userOrOrgId: userId || '',
      appId: selectedApp,
      filters: [filters],
      ranks
    });
    const estimatedInputCount = resp.hit_counts[0].estimated_total || '0';
    setTotalInputCount(estimatedInputCount);
    setSelectedDatasetCount(Number.parseInt(estimatedInputCount, 10));
  }, [selectedApp]);
  useEffect(() => {
    if (selectedApp) {
      fetchInputsCount();
    }
  }, [selectedApp]);
  const modifiedOptions = ([{
    id: `default_dataset (All ${totalInputCount} inputs)`
  }, ...datasetOptions] as SelectOptions[]);
  const formattedOptions = useMemo(() => modifiedOptions.map(({
    id,
    content
  }) => {
    return {
      name: id,
      value: id,
      content
    };
  }), [modifiedOptions]);
  return <div className={batchPredictStyles}>
      <div className="modal-header">
        <div className="title">Batch Predict on App Inputs</div>
        <div className="description">Models are automatically deployed after training - see predictions on app inputs</div>
      </div>
      <Formik initialValues={{}} onSubmit={() => {}}>
        <div className="modal-body">
          <div className="app-selector">
            <div className="selector-title">Select an App</div>
            <div className="app-selector-dropdown">
              <SelectField name="app-selector" value={selectedApp} placeholder="app-name (current)" options={appsOptions} onChange={onAppsChange} />
            </div>
          </div>
          <div className="dataset-selector">
            <div className="selector-title">Select a Dataset</div>
            <div className="dataset-selector-dropdown">
              <SelectField name="dataset-selector" value={selectedDataset} placeholder="default-dataset" options={formattedOptions} onChange={onDatasetsChange} disabled={!selectedApp} />
            </div>
          </div>
          {selectedDatasetCount !== undefined && <div>Total input count: {selectedDatasetCount}</div>}
        </div>
      </Formik>
      <div className="modal-actions">
        <div>
          <Button variant="tertiary" onClick={closeBatchPredictArea}>
            Cancel
          </Button>
        </div>
        <div>
          <Button data-testid="try-inputs-button" onClick={onTryInputClick}>
            Try Inputs
          </Button>
        </div>
      </div>
    </div>;
};
const batchPredictStyles = "odules__Predictions__components__PredictionSidebar__BatchPredictModal__BatchPredictModal_tsx-batchPredictStyles-bosy8rz";

require("./BatchPredictModal.linaria.module.css!=!../../../../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./BatchPredictModal.tsx");